// OrderDetailModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Tag, Typography, List, Card, Space, Button, Select, Input, message, Avatar } from 'antd';
import {
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ShoppingOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';
import { apiRequest } from '../utils/apiRequest';
import { useCurrency } from '../contexts/CurrencyContext';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const OrderDetailsModal = ({ order, visible, onCancel, onItemComplete, isAdminView = false }) => {
  const { formatMoney } = useCurrency();
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [retryingItem, setRetryingItem] = useState(null);
  const [verifyingItem, setVerifyingItem] = useState(null);
  const [verifyResults, setVerifyResults] = useState({});

  const { TextArea } = Input;
  const { Option } = Select;

  // Admin edit functions
  const handleEditItem = (item) => {
    setEditingItem(item.id);
    setEditForm({
      status: item.status,
      notes: item.notes || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditForm({ status: '', notes: '' });
  };

  const handleSaveEdit = async () => {
    if (!editForm.status) {
      message.error('Status is required');
      return;
    }

    setLoading(true);
    try {
      await apiRequest({
        method: 'PATCH',
        url: `admin/order-items/${editingItem}`,
        data: {
          status: editForm.status,
          notes: editForm.notes
        }
      });

      message.success('Order item updated successfully');
      setEditingItem(null);
      setEditForm({ status: '', notes: '' });

    } catch (error) {
      console.error('Failed to update order item:', error);
      message.error('Failed to update order item');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? '—' : format(d, 'dd/MM/yyyy - HH:mm');
  };

  const getOrderTypeConfig = (status) => {
    const s = status?.toLowerCase();
    if (s === 'completed' || s === 'complete' || s === 'delivered') {
      return { color: '#52c41a', bgColor: '#f6ffed', borderColor: '#b7eb8f', icon: '✅' };
    } else if (s === 'processing') {
      return { color: '#1890ff', bgColor: '#e6f7ff', borderColor: '#91d5ff', icon: '⏳' };
    } else if (s === 'pending') {
      return { color: '#b45309', bgColor: '#fff7e6', borderColor: '#ffd591', icon: '⏳' };
    } else {
      return { color: '#f5222d', bgColor: '#fff1f0', borderColor: '#ffccc7', icon: '❌' };
    }
  };

  const getItemStatusDetails = (status) => {
    switch(status) {
      case 'pending':
        return {
          icon: '⏳',
          title: 'Queued for Processing',
          description: 'Item is waiting to be sent to provider',
          color: 'orange'
        };
      case 'processing':
        return {
          icon: '🔄',
          title: 'Processing',
          description: 'Item is being processed by service provider',
          color: 'blue'
        };
      case 'complete':
      case 'delivered':
        return {
          icon: '✅',
          title: status === 'complete' ? 'Completed' : 'Delivered',
          description: status === 'complete' ? 'Item processed successfully' : 'Recharge/Delivery completed',
          color: 'green'
        };
      case 'failed':
        return {
          icon: '❌',
          title: 'Failed',
          description: 'Processing failed - check provider/system issues',
          color: 'red'
        };
      default:
        return {
          icon: '❓',
          title: status.charAt(0).toUpperCase() + status.slice(1),
          description: 'Status uncertain',
          color: 'default'
        };
    }
  };

  const Amount = ({ amount, strong = true, size = 16, currency = order?.currency }) => {
    return (
      <Text strong={strong} style={{ color: '#52c41a', fontSize: size, fontWeight: 600 }}>
        {formatMoney(parseFloat(amount || 0), currency)}
      </Text>
    );
  };

  const StatusBadge = ({ status }) => {
    const config = getItemStatusDetails(status);
    return (
      <Tag color={config.color} style={{ borderRadius: 12, padding: '2px 8px', fontSize: '12px', fontWeight: 500 }}>
        {config.icon} {config.title}
      </Tag>
    );
  };

  // Manual retry function for failed order items
  const handleRetryItem = async (item) => {
    setRetryingItem(item.id);
    try {
      await apiRequest({
        method: 'POST',
        url: `admin/orders/${order.id}/items/${item.id}/retry`,
        endpointType: 'main'
      });

      message.success('Order item retry initiated successfully');
      setTimeout(() => {
        onCancel(); // Close modal to refresh data
      }, 1500);

    } catch (error) {
      console.error('Failed to retry item:', error);
      message.error(error?.response?.data?.message || 'Failed to retry order item');
    } finally {
      setRetryingItem(null);
    }
  };

  const handleVerifyTransaction = async (item) => {
    setVerifyingItem(item.id);
    try {
      const result = await apiRequest({
        method: 'GET',
        url: `admin/order-items/${item.id}/verify-hotrecharge`
      });
      setVerifyResults(prev => ({ ...prev, [item.id]: result.data }));
    } catch (error) {
      message.error('Failed to verify transaction with HotRecharge');
      setVerifyResults(prev => ({ ...prev, [item.id]: { error: true } }));
    } finally {
      setVerifyingItem(null);
    }
  };

  const config = getOrderTypeConfig(order?.status);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            style={{
              backgroundColor: config.bgColor,
              color: config.color,
              border: `2px solid ${config.borderColor}`
            }}
            size={48}
          >
            <ShoppingOutlined />
          </Avatar>
          <div>
            <Title level={4} style={{ margin: 0, color: '#1f2937' }}>
              Order #{order?.order_number}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {formatDate(order?.created_at)}
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel} size="large">
          Close
        </Button>,
      ]}
      width={800}
      style={{ borderRadius: 16 }}
      styles={{ body: { borderRadius: 16 } }}
    >
      {/* Order Summary Card */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
        styles={{ body: { padding: '20px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
              Order Summary
            </Title>
            <Text type="secondary">
              {order?.items?.length || 0} item{(order?.items?.length || 0) !== 1 ? 's' : ''}
            </Text>
          </div>
          <StatusBadge status={order?.status} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              Total Amount
            </Text>
            <Amount amount={order?.total} size={20} />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              Payment Method
            </Text>
            <Text strong style={{ color: '#1890ff' }}>
              {order?.payment_method}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              Payment Status
            </Text>
            <Text strong style={{ color: order?.payment_status === 'complete' ? '#52c41a' : '#fa8c16' }}>
              {order?.payment_status || 'pending'}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              Channel
            </Text>
            {(() => {
              const ch = order?.channel || 'webapp'
              const isWhatsApp = ch === 'whatsapp'
              return (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                  color: isWhatsApp ? '#25d366' : '#1890ff',
                  background: isWhatsApp ? '#f0fff4' : '#e6f7ff',
                  border: `1px solid ${isWhatsApp ? '#25d36633' : '#1890ff33'}`
                }}>
                  {isWhatsApp ? '💬' : '🌐'} {ch}
                </span>
              )
            })()}
          </div>
        </div>
      </Card>

      {/* Order Items Card */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '18px' }}>📦</span>
            <span>Order Items & Status</span>
          </div>
        }
        style={{
          marginBottom: 16,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
        styles={{ body: { padding: '20px' } }}
      >
        {/* Admin Controls */}
        {isAdminView && (
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae7ff',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <Text strong style={{ fontSize: '14px', color: '#1890ff', display: 'block', marginBottom: '8px' }}>
              🛠️ Admin Controls
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Use the edit buttons below to update individual item statuses and add administrative notes.
            </Text>
            {editingItem && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveEdit}
                  loading={loading}
                >
                  Save Changes
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {order?.items?.map((item, index) => (
            <Card
              key={item.id || index}
              size="small"
              style={{
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '1px solid #f0f0f0'
              }}
              styles={{ body: { padding: '16px' } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar
                  style={{
                    backgroundColor: getItemStatusDetails(item.status).color === 'green' ? '#f6ffed' :
                                   getItemStatusDetails(item.status).color === 'red' ? '#fff1f0' :
                                   getItemStatusDetails(item.status).color === 'blue' ? '#e6f7ff' : '#fff7e6',
                    color: getItemStatusDetails(item.status).color === 'green' ? '#52c41a' :
                           getItemStatusDetails(item.status).color === 'red' ? '#f5222d' :
                           getItemStatusDetails(item.status).color === 'blue' ? '#1890ff' : '#fa8c16',
                    border: `1px solid ${getItemStatusDetails(item.status).color === 'green' ? '#b7eb8f' :
                                         getItemStatusDetails(item.status).color === 'red' ? '#ffccc7' :
                                         getItemStatusDetails(item.status).color === 'blue' ? '#91d5ff' : '#ffd591'}`
                  }}
                  size={40}
                >
                  {getItemStatusDetails(item.status).icon}
                </Avatar>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text strong style={{ fontSize: '16px', margin: 0 }}>
                      {item.product_name}
                    </Text>
                    <Amount amount={item.price} size={16} />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {getItemStatusDetails(item.status).description}
                    </Text>
                    <StatusBadge status={item.status} />
                  </div>

                  {item.account && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                      Account: {item.account}
                    </Text>
                  )}

                  {/* The notification number is who should receive the ZESA token.
                      It was captured and stored but never shown anywhere, so after
                      a failed order staff had no way to identify or contact the
                      intended recipient. Shown regardless of item status for
                      exactly that reason. */}
                  {item.notifyNumber && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                      Notification number: {item.notifyNumber}
                    </Text>
                  )}

                  {/* Admin Edit Form */}
                  {isAdminView && editingItem === item.id && (
                    <div style={{
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #e6e6e6',
                      padding: '12px',
                      borderRadius: '6px',
                      marginTop: '12px'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Status
                        </Text>
                        <Select
                          size="small"
                          value={editForm.status}
                          onChange={(value) => setEditForm({ ...editForm, status: value })}
                          style={{ width: '100%' }}
                        >
                          <Option value="pending">⏳ Pending</Option>
                          <Option value="processing">🔄 Processing</Option>
                          <Option value="complete">✅ Complete</Option>
                          <Option value="delivered">🚚 Delivered</Option>
                          <Option value="cancelled">❌ Cancelled</Option>
                          <Option value="failed">💥 Failed</Option>
                        </Select>
                      </div>

                      <div>
                        <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                          Notes
                        </Text>
                        <TextArea
                          size="small"
                          placeholder="Add administrative notes..."
                          value={editForm.notes}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          rows={2}
                          maxLength={500}
                          showCount
                        />
                      </div>
                    </div>
                  )}

                  {/* Edit Button */}
                  {isAdminView && editingItem !== item.id && (
                    <div style={{ textAlign: 'right', marginTop: '8px' }}>
                      <Button
                        size="small"
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEditItem(item)}
                        style={{ padding: '4px 8px' }}
                      >
                        Edit Status & Notes
                      </Button>
                    </div>
                  )}

                  {/* Mark Complete Button for Manual Processing Items */}
                  {isAdminView && item.service_provider_type === 'manual' && item.status === 'processing' && (
                    <div style={{ textAlign: 'right', marginTop: '8px' }}>
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={async () => {
                          try {
                            await apiRequest({
                              method: 'PATCH',
                              url: `admin/order-items/${item.id}`,
                              data: { status: 'complete', notes: 'Manually completed by admin' }
                            })
                            message.success('Item marked as complete')
                            if (onItemComplete) onItemComplete()
                            if (onCancel) onCancel()
                          } catch (err) {
                            message.error('Failed to mark item complete')
                          }
                        }}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Mark Complete
                      </Button>
                    </div>
                  )}

                  {/* Manual Retry Button for Admins */}
                  {isAdminView && item.status === 'failed' && (
                    <div style={{ textAlign: 'right', marginTop: '8px' }}>
                      <Button
                        size="small"
                        type="primary"
                        danger
                        icon={<SyncOutlined />}
                        onClick={() => handleRetryItem(item)}
                        loading={retryingItem === item.id}
                        disabled={retryingItem === item.id}
                      >
                        {retryingItem === item.id ? 'Retrying...' : 'Retry Item'}
                      </Button>
                    </div>
                  )}

                  {/* Verify Transaction Button (HotRecharge items only) */}
                  {isAdminView && item.service_provider_type === 'hotrecharge' && (
                    <div style={{ textAlign: 'right', marginTop: '8px' }}>
                      <Button
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() => handleVerifyTransaction(item)}
                        loading={verifyingItem === item.id}
                        disabled={!item.agent_reference}
                        title={!item.agent_reference ? 'No agent reference saved for this item' : 'Check live status on HotRecharge'}
                      >
                        Verify Transaction
                      </Button>
                    </div>
                  )}

                  {/* HotRecharge verification result */}
                  {verifyResults[item.id] && (
                    <div style={{
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #91d5ff',
                      padding: '10px',
                      borderRadius: '6px',
                      marginTop: '8px',
                      fontSize: '12px'
                    }}>
                      <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        🔍 HotRecharge Verification
                      </Text>
                      {verifyResults[item.id].error ? (
                        <Text type="danger">Failed to fetch result from HotRecharge.</Text>
                      ) : !verifyResults[item.id].found ? (
                        <Text type="warning">{verifyResults[item.id].message}</Text>
                      ) : (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '11px' }}>
                          {JSON.stringify(verifyResults[item.id].hotRechargeResult, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}

                  {/* Status-specific content */}
                  {item.status === 'failed' && item.details && (
                    <div style={{
                      backgroundColor: '#fff1f0',
                      border: '1px solid #ffccc7',
                      padding: '8px',
                      borderRadius: '4px',
                      marginTop: '8px'
                    }}>
                      <Text type="danger" style={{ fontSize: '11px' }}>
                        {(() => {
                          try {
                            const details = typeof item.details === 'string' ? JSON.parse(item.details) : item.details;
                            return details.message || 'Processing failed';
                          } catch (e) {
                            return item.details;
                          }
                        })()}
                      </Text>
                    </div>
                  )}

                  {(item.status === 'complete' || item.status === 'delivered') && (
                    <div style={{
                      backgroundColor: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      padding: '8px',
                      borderRadius: '4px',
                      marginTop: '8px'
                    }}>
                      <Text type="success" style={{ fontSize: '11px' }}>
                        Service delivered successfully
                      </Text>
                      {(() => {
                        try {
                          const details = typeof item.details === 'string' ? JSON.parse(item.details) : item.details;
                          const rd = details?.rechargeData;
                          if (rd?.Token) {
                            return (
                              <div style={{ marginTop: '6px' }}>
                                <div><Text strong style={{ fontSize: '11px' }}>Token: </Text><Text style={{ fontSize: '11px', fontFamily: 'monospace' }}>{rd.Token}</Text></div>
                                {rd.Units && <div><Text strong style={{ fontSize: '11px' }}>Units: </Text><Text style={{ fontSize: '11px' }}>{rd.Units} kWh</Text></div>}
                              </div>
                            );
                          }
                        } catch (e) {}
                        return null;
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>
    </Modal>
  );
};

export default OrderDetailsModal;
